import { useState } from 'react';
import { ImagePlus, RotateCcw, X, Check } from 'lucide-react';
import { useSiteConfig, saveSiteConfig, fileToDataUrl } from '../../../utils/siteConfig';
import { useQueue } from '../../../context/QueueContext';

function PreviewFrame({ src, mobile }) {
  return (
    <div
      className={`flex items-center border border-navy/10 bg-white px-4 ${mobile ? 'h-14 rounded-xl' : 'h-20 rounded-t-xl'}`}
      data-testid={mobile ? 'pers-logo-preview-mobile' : 'pers-logo-preview-desktop'}
    >
      <img src={src} alt="Prévia da logo" className={`w-auto max-w-full object-contain ${mobile ? 'h-9' : 'h-12'}`} />
    </div>
  );
}

export default function IdentitySection({ flash }) {
  const { user } = useQueue();
  const cfg = useSiteConfig();
  const current = cfg.identity.logoImage;
  const [pending, setPending] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPending(await fileToDataUrl(file, 800));
    e.target.value = '';
  };

  const apply = async () => {
    try {
      await saveSiteConfig({ identity: { logoImage: pending } }, user, 'Alterou a logo do site.');
      setPending(null);
      flash('Logo atualizada.');
    } catch (e) {
      flash(e.message || 'Falha ao salvar a logo.');
    }
  };

  const restore = async () => {
    try {
      await saveSiteConfig({ identity: { logoImage: null } }, user, 'Restaurou a logo padrão do site.');
      setPending(null);
      flash('Logo padrão restaurada.');
    } catch (e) {
      flash(e.message || 'Falha ao restaurar a logo.');
    }
  };

  return (
    <section className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="pers-identity-section">
      <h3 className="font-serif text-xl text-navy">Identidade — Logo</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        A logo aparece no cabeçalho em desktop e mobile, sempre preservando a proporção da imagem.
      </p>

      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Logo atual</p>
      <div className="mt-2 rounded-xl border border-navy/10 bg-navy-light/40 p-4" data-testid="pers-logo-current">
        {current ? (
          <img src={current} alt="Logo atual" className="h-12 w-auto object-contain" />
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="pers-logo-default-note">
            Logo padrão (texto “HUDDERSFIELD TECIDOS”).
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <label
          htmlFor="pers-logo-upload"
          data-testid="pers-logo-upload-label"
          className="flex cursor-pointer items-center gap-2 rounded-full border border-navy/20 px-5 py-3 text-sm font-semibold text-navy hover:bg-navy-light"
        >
          <ImagePlus className="h-4 w-4" /> ENVIAR NOVA LOGO
        </label>
        <input
          id="pers-logo-upload"
          data-testid="pers-logo-upload-input"
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={onFile}
        />
        {current && !pending && (
          <button
            type="button"
            onClick={restore}
            data-testid="pers-logo-restore"
            className="flex items-center gap-2 rounded-full border border-red-300 px-5 py-3 text-sm font-semibold text-red-700 hover:bg-red-50"
          >
            <RotateCcw className="h-4 w-4" /> RESTAURAR PADRÃO
          </button>
        )}
      </div>

      {pending && (
        <div className="mt-5 rounded-2xl border border-navy/15 bg-navy-light/30 p-4" data-testid="pers-logo-pending">
          <p className="text-sm font-semibold text-navy">Pré-visualizar antes de aplicar</p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Desktop</p>
          <div className="mt-1"><PreviewFrame src={pending} /></div>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mobile</p>
          <div className="mt-1 max-w-xs"><PreviewFrame src={pending} mobile /></div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:flex">
            <button
              type="button"
              onClick={() => setPending(null)}
              data-testid="pers-logo-cancel"
              className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-5 py-3 text-sm font-semibold text-navy hover:bg-white"
            >
              <X className="h-4 w-4" /> CANCELAR
            </button>
            <button
              type="button"
              onClick={apply}
              data-testid="pers-logo-apply"
              className="flex items-center justify-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
            >
              <Check className="h-4 w-4" /> APLICAR LOGO
            </button>
          </div>
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        A imagem é enviada ao armazenamento durável do servidor e aplicada em todos os dispositivos.
      </p>
    </section>
  );
}
